import { formatAssistantCell, type AssistantChartModel, type AssistantColumn, type ChartSeries } from "@teamna/shared";
import { formatDateTime } from "@teamna/web-client/lib/format.ts";

/**
 * The charts of AI Assistant answers (generic data plan, decisions A4 to A7), for Desk and Control
 * Center. `assistantChartModel` in `@teamna/shared` gives the bars, lines and tiles from the table
 * of the answer, so this file only draws: it never counts, adds or rounds. Plain boxes and one SVG
 * line, no chart library, the same look as the mobile app.
 */

/** Series colours, in order. Tokens of `styles.css`, so dark mode follows. */
const SERIES_COLOURS = [
  "var(--color-accent)",
  "var(--color-tile-came)",
  "var(--color-tile-booked)",
  "var(--color-tile-quiet)",
  "var(--color-tile-run)",
  "var(--color-freeze)",
  "var(--color-accent-soft)",
  "var(--color-late-gold)",
];

function colourOf(index: number): string {
  return SERIES_COLOURS[index % SERIES_COLOURS.length]!;
}

function valueText(value: number | string | null, kind: AssistantColumn["kind"], timezone: string): string {
  return formatAssistantCell(value, kind, timezone, formatDateTime);
}

/** A partial bar (the period is not over) is striped, so a low bar does not look like a bad week. */
function fill(colour: string, partial: boolean): string {
  return partial ? `repeating-linear-gradient(135deg, ${colour} 0 6px, color-mix(in srgb, ${colour} 40%, transparent) 6px 12px)` : colour;
}

function Legend({ series }: { series: ChartSeries[] }) {
  if (series.length < 2) return null;
  return (
    <ul className="flex flex-wrap gap-x-4 gap-y-1 pb-2 text-[12.5px] text-ink-2">
      {series.map((item, index) => (
        <li key={item.key} className="flex items-center gap-1.5">
          <span aria-hidden className="inline-block size-2.5 rounded-sm" style={{ background: colourOf(index) }} />
          <span dir="auto">{item.label}</span>
        </li>
      ))}
    </ul>
  );
}

function share(value: number | null, max: number): string {
  return `${Math.max(0, Math.min(100, ((value ?? 0) / max) * 100))}%`;
}

/** Bars across: one row for each name, the value at the end. */
function BarsAcross({ model, timezone }: { model: Extract<AssistantChartModel, { across: boolean }>; timezone: string }) {
  const stacked = model.kind === "stacked_bar";
  return (
    <ul className="space-y-2">
      {model.axis.map((at, row) => {
        const values = model.series.map((item) => item.values[row] ?? null);
        // The values of the table, also for stacked parts: the chart never adds them up.
        const label = values.map((value) => valueText(value, model.kindOfValues, timezone)).join(" · ");
        return (
          <li key={row} className="grid grid-cols-[minmax(70px,140px)_1fr_auto] items-center gap-2.5 text-[13.5px]" aria-label={`${at.label}: ${label}`}>
            <span dir="auto" className="truncate font-semibold">
              {at.label}
            </span>
            {stacked ? (
              <span className="flex h-3 overflow-hidden rounded-full bg-sunken">
                {model.series.map((item, index) => (
                  <span key={item.key} style={{ width: share(item.values[row] ?? null, model.max), background: fill(colourOf(index), at.partial) }} />
                ))}
              </span>
            ) : (
              <span className="space-y-1">
                {model.series.map((item, index) => (
                  <span key={item.key} className="block h-3 overflow-hidden rounded-full bg-sunken">
                    <span className="block h-full rounded-full" style={{ width: share(item.values[row] ?? null, model.max), background: fill(colourOf(index), at.partial) }} />
                  </span>
                ))}
              </span>
            )}
            <span className="text-ink-2 tabular-nums">{label}</span>
          </li>
        );
      })}
    </ul>
  );
}

/** Bars up: one column for each day, week or month, in time order. */
function BarsUp({ model, timezone }: { model: Extract<AssistantChartModel, { across: boolean }>; timezone: string }) {
  const stacked = model.kind === "stacked_bar";
  return (
    <ul className="flex h-44 items-end gap-1.5">
      {model.axis.map((at, column) => {
        const values = model.series.map((item) => item.values[column] ?? null);
        // A stacked column shows no number on top: the table has no total for it.
        const label = values.map((value) => valueText(value, model.kindOfValues, timezone)).join(" · ");
        return (
          <li key={column} aria-label={`${at.label}: ${label}${at.partial ? ", up to now" : ""}`} className="flex h-full min-w-0 flex-1 flex-col items-center justify-end gap-1">
            {!stacked && model.axis.length <= 16 ? <span className="text-[11.5px] font-bold text-accent-strong tabular-nums">{label}</span> : null}
            <div className={`flex w-full max-w-10 items-end ${stacked ? "flex-col-reverse justify-start" : "gap-0.5"}`} style={{ height: "100%" }}>
              {model.series.map((item, index) => (
                <span
                  key={item.key}
                  className={stacked ? "w-full first:rounded-b-sm last:rounded-t-sm" : "flex-1 rounded-t-sm"}
                  style={{ height: share(item.values[column] ?? null, model.max), background: fill(colourOf(index), at.partial) }}
                />
              ))}
            </div>
            <span className="max-w-full truncate text-[11px] text-ink-2">{at.label}</span>
          </li>
        );
      })}
    </ul>
  );
}

const LINE_W = 600;
const LINE_H = 180;

/** A line for a long time trend: the first, the middle and the last day under it. */
function Line({ model, timezone }: { model: Extract<AssistantChartModel, { across: boolean }>; timezone: string }) {
  const count = model.axis.length;
  const x = (index: number) => (count === 1 ? LINE_W / 2 : (index / (count - 1)) * LINE_W);
  const y = (value: number) => LINE_H - (value / model.max) * LINE_H;
  const ticks = [...new Set([0, Math.floor((count - 1) / 2), count - 1])];
  return (
    <div>
      {/* The words under the line say the first, middle and last values; the table has them all. */}
      <svg viewBox={`-4 -8 ${LINE_W + 8} ${LINE_H + 16}`} className="h-44 w-full" preserveAspectRatio="none" aria-hidden>
        <line x1={0} x2={LINE_W} y1={LINE_H} y2={LINE_H} stroke="var(--color-line-strong)" strokeWidth={1} vectorEffect="non-scaling-stroke" />
        {model.series.map((item, index) => {
          // A missing value breaks the line: no point is made up.
          const parts: string[][] = [[]];
          item.values.forEach((value, at) => (value === null ? parts.push([]) : parts.at(-1)!.push(`${x(at)},${y(value)}`)));
          return parts
            .filter((points) => points.length > 0)
            .map((points, part) => (
              <polyline
                // The parts of one line never change order.
                // oxlint-disable-next-line react/no-array-index-key
                key={`${item.key}-${part}`}
                points={points.join(" ")}
                fill="none"
                stroke={colourOf(index)}
                strokeWidth={2.5}
                strokeLinejoin="round"
                vectorEffect="non-scaling-stroke"
              />
            ));
        })}
      </svg>
      <div className="relative h-4 text-[11px] text-ink-2">
        {ticks.map((at) => (
          <span key={at} className="absolute -translate-x-1/2 whitespace-nowrap first:translate-x-0 last:-translate-x-full" style={{ left: `${count === 1 ? 50 : (at / (count - 1)) * 100}%` }}>
            {model.axis[at]!.label}
          </span>
        ))}
      </div>
      <p className="pt-1 text-[12.5px] text-ink-2 tabular-nums">
        Last: {model.series.map((item) => `${model.series.length > 1 ? `${item.label} ` : ""}${valueText(item.values.at(-1) ?? null, item.kind, timezone)}`).join(" · ")}
        {model.axis.at(-1)?.partial ? " (up to now)" : ""}
      </p>
    </div>
  );
}

/** KPI tiles: the value big, the value before and the change under it. */
function Tiles({ model, timezone }: { model: Extract<AssistantChartModel, { kind: "kpi" }>; timezone: string }) {
  return (
    <ul className="grid grid-cols-2 gap-2 sm:grid-cols-4">
      {model.tiles.map((tile) => {
        const change = typeof tile.change === "number" ? tile.change : null;
        const unit = tile.kind === "percent" ? " points" : "";
        return (
          <li key={tile.key} className="rounded-lg border border-line bg-surface px-3 py-2.5">
            <p dir="auto" className="text-[12.5px] text-ink-2">
              {tile.label}
            </p>
            <p className="text-[22px] leading-tight font-bold tabular-nums">{valueText(tile.value, tile.kind, timezone) || "None"}</p>
            {tile.previous !== null ? (
              <p className="text-[12.5px] text-ink-2 tabular-nums">
                {valueText(tile.previous, tile.kind, timezone)} before
                {change === null || change === 0 ? "" : `, ${change > 0 ? "up" : "down"} ${tile.kind === "money" ? valueText(Math.abs(change), "money", timezone) : Math.abs(change)}${unit}`}
              </p>
            ) : null}
          </li>
        );
      })}
    </ul>
  );
}

export function AssistantChartView({ model, timezone }: { model: AssistantChartModel; timezone: string }) {
  if (model.kind === "kpi") return <Tiles model={model} timezone={timezone} />;
  return (
    <div>
      <Legend series={model.series} />
      {model.kind === "line" ? <Line model={model} timezone={timezone} /> : model.across ? <BarsAcross model={model} timezone={timezone} /> : <BarsUp model={model} timezone={timezone} />}
    </div>
  );
}
